import { OrgBaseInterface } from "../Shared/OrgBase.Interace";

export enum BotMessageRole {
  USER = "user",
  ASSISTANT = "assistant",
  SYSTEM = "system"
}

export enum BotMessageType {
  MESSAGE = "message",
  SYSTEM_PROMPT = "system_prompt",
  LLM_CALL = "llm_call",
  TOOL_CALL = "tool_call",
  SESSION_RESUME = "session_resume",
  PAGE_NAVIGATION = "page_navigation",
  ANALYTICS_EVENT = "analytics_event",
  SANDBOX_ACTION = "sandbox_action",
  /** Opening-turn priming text for an outbound sequence. Instruction to the bot, not a user message. */
  SEQUENCE_TRIGGER = "sequence_trigger",
  /** Greeting trigger text injected to prompt the bot's opening message. */
  GREETING_TRIGGER = "greeting_trigger",
  /**
   * Invisible event recording a direct widget→server call into the embed picker API
   * (e.g. radius filter, change-location, units fetch via chainTo). Records params +
   * returned ids + latency in `metadata` so admins can inspect picker activity that
   * happened outside the LLM tool-call path. Per docs/snowy/plans/.../A4 in the widget
   * build-out plan.
   */
  PICKER_API_CALL = "picker_api_call",
  /**
   * Step in the escalation / routing chain. Written from both bot and server
   * sites as the user's request moves through `escalate_to_team` → routing
   * resolution → assignment offer → accept/reject/timeout → bridge → completion
   * (or fallthrough to ticket-only). `metadata.subType` discriminates the step
   * — see docs/snowy/plans/2026-06-09-voice-escalation-barge-in.md for the full
   * subtype catalog and per-subtype metadata shape. Lets admins + agents see
   * the full ordered story in the Studio debug timeline alongside LLM_CALL +
   * TOOL_CALL events.
   */
  ESCALATION_EVENT = "escalation_event",
  /**
   * Invisible event recording a coaching-nudge action side effect (ENG-2957),
   * e.g. the agent texting the caller mid-voice-call — the call transcript
   * reflects the cross-channel send without leaking to the widget wire.
   */
  NUDGE_ACTION = "nudge_action",
  /**
   * The priming record for a Conversation Starter fire (ENG-3110): the context text
   * the bot was primed with, plus caller metadata (starter slug, campaign id, …).
   * Admin-only machinery — never a user message, never shown in the chat replay.
   * Spec: docs/specs/conversation-starters.md.
   */
  STARTER_TRIGGER = "starter_trigger",
  /**
   * The priming record for a widget-CTA opening (ENG-3166): the system-built why-context
   * plus the CTA type / id / action. Admin-only machinery, like `STARTER_TRIGGER`.
   */
  CTA_TRIGGER = "cta_trigger",
  /**
   * Generic error/problem marker (ENG-3110): something went wrong inside the
   * conversation and a fallback was taken — e.g. a welcome message referenced a
   * variable with no value, or a starter slug didn't resolve. `metadata.source`
   * names the origin, `metadata.detail` the specifics. Deliberately visible to
   * agents as well as Swivl staff and admins (Chuck, 2026-08-23) — it is NOT on
   * `ADMIN_ONLY_MESSAGE_TYPES`.
   */
  PROBLEM = "problem"
}

/** Transmission state of a message. See docs/snowy/plans/2026-06-16-message-delivery-status-seen-and-optout.md. */
export enum BotMessageDeliveryStatus {
  SENDING = "sending",
  QUEUED = "queued",
  SENT = "sent",
  DELIVERED = "delivered",
  UNDELIVERED = "undelivered",
  FAILED = "failed",
  /** Pre-Outbound-Gate name for a non-production hold-back; rows written before ENG-3373 keep it. */
  SANDBOXED = "sandboxed",
  /** Pre-Outbound-Gate name for a consent block; rows written before ENG-3373 keep it. New rows use `BLOCKED`. */
  OPTED_OUT = "opted_out",
  /** Outbound Gate (ENG-3373): built and recorded, never sent — flag off or recipient not allowed. */
  SIMULATED = "simulated",
  /** Outbound Gate: the person opted out (decision 7). */
  BLOCKED = "blocked"
}

/** Per-message read receipts. Multiple agents can each see one message. */
export interface BotMessageSeenBy {
  /** When the user / end-user read it. */
  user?: { at: string };
  /** Each agent member who read it, keyed by memberId → when. */
  members?: { [memberId: string]: { at: string } };
}

/** Non-queried delivery + seen metadata, carried in the `delivery` JSONB column. */
export interface BotMessageDelivery {
  /** Twilio numeric ErrorCode, or a local reason (opt-out, sandbox). */
  errorCode?: string;
  /** ISO timestamp of the last delivery-status write. */
  updatedAt?: string;
  /** Per-message read receipts (user + agents). */
  seenBy?: BotMessageSeenBy;
  /** Optional raw channel receipt payloads, for debugging. */
  receipts?: Record<string, unknown>;
}

export interface BotMessageInterface extends OrgBaseInterface {
  botConversationId: number;
  role: BotMessageRole;
  content: string;
  contentBlocks?: any[];
  /** Monotonic position within the conversation. "sequence" is reserved for the Sequences product. */
  messageIndex: number;
  messageId: string;
  /** Twilio MessageSid for outbound SMS — the status-webhook lookup key. */
  externalSid?: string;
  /** Transmission state. NULL until set (e.g. inbound / system rows). */
  deliveryStatus?: BotMessageDeliveryStatus;
  /** Non-queried delivery + seen metadata (error code, timestamps, seenBy, receipts). */
  delivery?: BotMessageDelivery;
  tokenInput?: number;
  tokenOutput?: number;
  /** Message type — "message" for normal messages, "llm_call" and "tool_call" for events */
  type?: BotMessageType;
  /** Whether this message is visible in the normal conversation view */
  visible?: boolean;
  /** Structured metadata for event messages (LLM call details, tool call details) */
  metadata?: any;
  /** Reference to the BotLLMCall record for llm_call event messages */
  botLLMCallId?: number;
  /** Reference to the BotUserAnalyticsEvent record for analytics_event messages */
  botAnalyticsEventId?: number;

  // ─── Structured-message protocol (2026-04-28) ──────────────────────────
  // See docs/snowy/plans/2026-04-28-structured-message-protocol.md §7.1.
  // Application code interprets NULL `contentType` on `type='message'` rows
  // as legacy text (pre-protocol).

  /** Top-level discriminator for chat messages: "text" | "structured". */
  contentType?: "text" | "structured" | null;
  /** Structured envelope content — `{ kind, data }` per protocol §2. */
  payload?: Record<string, unknown> | null;
  /**
   * Recorded user actions on this message. Single object for structured
   * (`StructuredActionTaken`); array for text (`LinkClick[]`). NULL when none.
   */
  actionTaken?: Record<string, unknown> | Record<string, unknown>[] | null;
}
